import React, { useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import {
  Typography,
  Box,
  Button,
  Icon,
  Stack,
  FormHelperText,
  FormControl,
  InputLabel,
  Divider,
  Skeleton,
} from "@mui/material";
import ActionCard from "../blocks/ActionCard.js";
import { LoadingButton } from "../blocks/LoadingButton.js";
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiRoute, getPageRoute } from "src/services/Config.js";
import { useTheme } from "@emotion/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import { Helmet } from "react-helmet";
import { customEvent } from "src/utils/gtag.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import Countdown from "react-countdown";

const DEFAULT_SUCCESS_URL = getPageRoute(
  "upgrade",
  "PAYMENT_STANZA_PLUS_CONFIRMATION"
);

const PaymentCheckout = ({
  type = "consumer_annual",
  trialPeriodDays = 7,
  successUrl = DEFAULT_SUCCESS_URL,
}) => {
  const { user, refetchAuth } = useContext(AuthContext);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const location = useLocation();
  const { calendar = null } = location.state || {};

  const { subscribe } = useSubscribe();

  const theme = useTheme();

  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);

  const fetchPriceDetails = async () => {
    if (stripe && type) {
      try {
        const response = await axios.get(
          getApiRoute("upgrade", "STRIPE_PRICE_DETAILS", {}, { type })
        );
        return response.data;
      } catch (error) {
        setError("Failed to retrieve price details.");
      }
    }
  };

  const {
    data: priceDetails,
    isLoading: isLoadingPriceDetails,
    refetch: refetchStripePriceDetails,
  } = useQuery([`stripe-price-details`], fetchPriceDetails, {
    enabled: !!stripe,
  });

  useEffect(() => {
    if (user.hasValidSubscription) {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "You already have an active Stanza+ subscription.",
        "success"
      );
      setTimeout(() => {
        navigate(getPageRoute("home", "HOME"));
      }, 2000);
    }
  }, [user]);

  useEffect(() => {
    customEvent({
      name: "native_checkout_component_loaded",
      category: "NativeCheckout",
      label: "Native Checkout Component Loaded",
      data: {
        user_id: user?._id,
        user_email: user?.email,
      },
    });
  }, []);

  useEffect(() => {
    refetchStripePriceDetails();
  }, [stripe]);

  const {
    mutateAsync: initializePaymentRequest,
    isLoading: isLoadingPaymentRequest,
    isError: isErrorPaymentRequest,
    error: errorPaymentRequest,
  } = useMutation(async () => {
    if (stripe && priceDetails) {
      const { amount, label, currency } = priceDetails;

      const pr = stripe.paymentRequest({
        country: "US",
        currency: currency || "usd",
        total: {
          label: trialPeriodDays > 0 ? `${trialPeriodDays} free days` : label,
          amount: trialPeriodDays > 0 ? 0 : amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ["link"],
      });

      const result = await pr.canMakePayment();
      if (result) {
        setPaymentRequest(pr);
      } else {
        setPaymentRequest(null);
      }
    }
  });

  useEffect(() => {
    if (stripe && priceDetails) {
      initializePaymentRequest();
    }
  }, [stripe, priceDetails]);

  const trackPaymentFailure = useCallback(
    (message, error = new Error(), description = "", logToRollbar = true) => {
      customEvent({
        name: `native_payment_failure`,
        category: "Upgrade",
        label: "Failure Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
          message,
        },
      });

      if (logToRollbar) {
        rollbar.error(description || "Native Payment Failure", error, {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
          message,
          hasStripeInstance: Boolean(stripe),
          hasElements: Boolean(elements),
        });
      }
    },
    [elements, stripe, trialPeriodDays, user._id, user.email, user.handle]
  );

  useEffect(() => {
    if (isErrorPaymentRequest) {
      trackPaymentFailure(
        errorPaymentRequest?.message,
        errorPaymentRequest,
        "Failure on initializePaymentRequest"
      );
      setError("Failed to initialize payment request.");
    }
  }, [errorPaymentRequest, isErrorPaymentRequest]);

  const handlePayment = async ({
    isPaymentRequest,
    paymentMethodId,
    event,
  }) => {
    if (!isPaymentRequest) event.preventDefault();
    setLoading(true);

    try {
      if (trialPeriodDays > 0) {
        customEvent({
          name: "trial_period_payment",
          category: "Upgrade",
          label: "Requested Trial Period Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
          },
        });
      }

      // Use the provided payment method ID or create one using the CardElement
      let methodId = paymentMethodId;

      if (!isPaymentRequest) {
        if (!stripe) {
          // Error in stripe instance
          throw new Error("Stripe has not loaded yet.");
        }
        if (!elements) {
          // Error in stripe elements
          throw new Error("Stripe elements has not loaded yet.");
        }

        if (cardError || !cardComplete) {
          setError("Please enter your card details.");
          setLoading(false);
          trackPaymentFailure("Invalid card details", null, "", false);
          return;
        }

        const { error: stripeError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement),
            billing_details: {
              email: user.email,
            },
          });

        if (stripeError) {
          // Error in createPaymentMethod
          throw stripeError;
        }

        methodId = paymentMethod.id;
      }

      // Proceed with the backend call
      const response = await axios.post(
        getApiRoute("upgrade", "STRIPE_NATIVE_CHECKOUT"),
        {
          type,
          paymentMethodId: methodId,
          trialPeriodDays,
        },
        { withCredentials: true }
      );

      const { clientSecret } = response.data;

      // Process the payment
      // When there is a new trial, there is no clientSecret generated as there is no payment
      if (clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret
        );
        if (confirmError) {
          // Error in confirmCardPayment
          throw confirmError;
        }
      } else if (!trialPeriodDays) {
        // Error in clientSecret
        throw new Error("Failed on starting checkout session.");
      }

      // Success: Update auth and navigate
      await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 sec to leave the backend update upgrade status
      await refetchAuth();
      navigate(successUrl);
      customEvent({
        name: `native_payment_successful`,
        category: "Upgrade",
        label: "Successful Native Payment",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          trial_period: trialPeriodDays > 0,
        },
      });
      event?.complete?.("success"); // Handle PaymentRequest event completion
      if (calendar) subscribe({ calendar });
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "An error occurred"
      );
      trackPaymentFailure(
        err.response?.data?.message || err.message || "An error occurred",
        err
      );
      event?.complete?.("fail"); // Handle PaymentRequest event completion
    } finally {
      setLoading(false); // always gets executed
    }
  };

  const handlePaymentMethodReceived = async (event) => {
    await handlePayment({
      isPaymentRequest: true,
      paymentMethodId: event.paymentMethod.id,
      event,
    });
  };

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on("paymentmethod", handlePaymentMethodReceived);
    }
  }, [paymentRequest]);

  const cardElementStyles = {
    base: {
      color: "#000000",
      fontSize: "16px",
      fontFamily: ["Satoshi", "Nunito", "Open Sans", "Arial"].join(","),
      "::placeholder": {
        color: "rgba(0, 0, 0, 0.54)",
      },
    },
    invalid: {
      color: "#FF4C4C",
    },
  };

  const cardElementWrapperStyles = {
    padding: "18.5px 14px",
    borderRadius: "4px",
    border: `1px solid ${
      cardFocused ? "#000000" : cardError ? "#f44336" : "rgba(0, 0, 0, 0.23)"
    }`,
    "&:hover": {
      borderColor: cardFocused ? "#000000" : "rgba(0, 0, 0, 0.87)",
    },
    position: "relative",
  };

  const SALE_DURATION_MS = 24 * 60 * 60 * 1000;
  const [saleCountdown] = useState({
    expiresAt: Date.now() + SALE_DURATION_MS,
    expired: false
  });
  const [countdownReady, setCountdownReady] = useState(false);

  // Small delay to ensure countdown renders properly
  useEffect(() => {
    const timer = setTimeout(() => setCountdownReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const CountdownRenderer = ({ hours, minutes, seconds, completed }) => {
    if (completed) {
      return <span>Expired</span>;
    }
    return (
      <span>
        {hours}h {minutes}m {seconds}s
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>Stanza - Payment Checkout</title>
      </Helmet>
      {isLoadingPriceDetails ? (
        <LoadingAnimation />
      ) : !priceDetails ? (
        <ActionCard
          title="No price details"
          description="Failed to retrieve price details."
          primaryButtonCTA="Go Home"
          primaryAction={() => navigate("/")}
          iconName="highlight_off"
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            id="container"
            sx={{
              margin: "0 auto 0 auto",
              maxWidth: { xs: "100%", sm: "400px" },
              width: { xs: "-webkit-fill-available", sm: "100%" },
              position: "relative",
              textAlign: "center",
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                display: "block",
                margin: "24px auto",
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "middle",
                fontSize: "3rem",
                mt: 5,
                mb: 1,
                fontWeight: 300,
              }}
            >
              payment
            </Icon>
            {priceDetails && (
              <>
                <Typography variant="title" sx={{ mb: 1 }}>
                  {priceDetails.label}
                </Typography>
                {trialPeriodDays > 0 ? (
                  <Box sx={{ mt: 0.5, mb: 4 }}>
                    <Typography variant="h6">
                      {trialPeriodDays} free days
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", mt: 1 }}
                    >
                      then{" "}
                      <span style={{ textDecoration: "line-through" }}>
                        $5.00
                      </span>{" "}
                      $2.50/mo (billed annually)
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: "success.main",
                        color: "success.main",
                        borderRadius: "16px",
                        padding: "4px 12px",
                        marginTop: "8px",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box component="span" sx={{ mr: 1 }}>
                          SALE ENDS SOON:
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            minWidth: "80px",
                            textAlign: "center",
                            fontVariantNumeric: "tabular-nums",
                            height: "20px",
                            lineHeight: "20px",
                          }}
                        >
                          {countdownReady ? (
                            <Countdown
                              date={saleCountdown.expiresAt}
                              renderer={CountdownRenderer}
                            />
                          ) : (
                            <Skeleton
                              animation="wave"
                              variant="text"
                              sx={{ 
                                py: 0,
                                width: "80px", 
                                height: "20px",
                                lineHeight: "20px",
                                display: "inline-block",
                                backgroundColor: "rgba(0, 0, 0, 0.11)"
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    for ${(priceDetails.amount / 100).toFixed(2)}{" "}
                    {priceDetails.recurring?.interval &&
                      `per ${priceDetails.recurring.interval}`}
                  </Typography>
                )}
              </>
            )}
            {isLoadingPaymentRequest ? (
              <Skeleton
                animation="wave"
                component="div"
                variant="rounded"
                sx={{ borderRadius: "4px", height: "40px", mb: 2.5 }}
              ></Skeleton>
            ) : paymentRequest ? (
              <>
                <PaymentRequestButtonElement
                  options={{ paymentRequest }}
                  style={{
                    paymentRequestButton: {
                      type: "default", // 'default' | 'donate' | 'buy' | 'book' | 'checkout' | 'subscribe'
                      theme: "dark", // 'dark' | 'light' | 'light-outline'
                    },
                  }}
                />
                <Divider sx={{ my: 2.5 }}>
                  <Typography variant="subtitle2">or pay with card</Typography>
                </Divider>
              </>
            ) : (
              <> </>
            )}

            <form
              onSubmit={(event) =>
                handlePayment({
                  event, // The submit event to prevent default behavior
                  isPaymentRequest: false, // Specify it's a CardElement payment
                  paymentMethodId: null, // No pre-existing payment method
                })
              }
            >
              <Stack spacing={2.5}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    shrink
                    error={!!cardError}
                    sx={{
                      backgroundColor: "#fff",
                      padding: "0 4px",
                      pointerEvents: "none",
                    }}
                  >
                    Card Details
                  </InputLabel>
                  <Box
                    sx={cardElementWrapperStyles}
                    onClick={() => {
                      const cardElement = elements.getElement(CardElement);
                      cardElement.focus();
                    }}
                  >
                    <CardElement
                      options={{
                        style: cardElementStyles,
                        hidePostalCode: true, // Simplify the fields
                        disableLink: true,
                      }}
                      onChange={(event) => {
                        setCardError(event.error ? event.error.message : null);
                        setCardComplete(event.complete);
                      }}
                      onFocus={() => setCardFocused(true)}
                      onBlur={() => setCardFocused(false)}
                    />
                  </Box>
                  {cardError && (
                    <FormHelperText error>{cardError}</FormHelperText>
                  )}
                </FormControl>
                {error && <Typography color="error">{error}</Typography>}
                <LoadingButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!stripe}
                  loading={loading}
                  fullWidth
                  sx={{ mt: 2, width: "100%" }}
                >
                  {trialPeriodDays > 0 ? "Pay $0 Today" : "Pay Now"}
                </LoadingButton>
                <Typography
                  variant="subtitle2"
                  sx={{ margin: "auto", color: "text.secondary", mb: 4 }}
                >
                  No commitment, cancel anytime.
                </Typography>
              </Stack>
            </form>
          </Box>
        </div>
      )}
    </>
  );
};

export default PaymentCheckout;
